import { useState, useCallback } from "react";
import { ImagePlus, Image as ImageIcon, Video, Maximize2, LayoutDashboard, CreditCard } from "lucide-react";
import { Sidebar, SidebarBody, SidebarLink, useSidebar } from "@/components/ui/aceternity-sidebar";
import { motion, AnimatePresence } from "framer-motion";
import { PricingModal } from "@/components/PricingModal";
import { useIsMobile } from "@/hooks/use-mobile";
import { BottomNavBar } from "@/components/BottomNavBar";
import { useAuth } from "@/contexts/AuthContext";
import LoginPromptModal from "@/components/LoginPromptModal";
interface AppSidebarProps {
  children: React.ReactNode;
}

// Pages that require authentication
const protectedPaths = ["/generator", "/video-generator", "/video-upscale", "/dashboard"];

export const AppSidebar = ({ children }: AppSidebarProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
  const [loginRedirect, setLoginRedirect] = useState<{ path: string; state?: Record<string, unknown> } | null>(null);
  const isMobile = useIsMobile();
  const { user } = useAuth();

  const handleProtectedNavigation = useCallback((href: string, e: React.MouseEvent) => {
    // Check if path is protected and user is not logged in
    if (protectedPaths.includes(href) && !user) {
      e.preventDefault();
      setLoginRedirect({ path: href });
      setIsLoginPromptOpen(true);
      return true;
    }
    return false;
  }, [user]);

  const navigationLinks = [
    {
      label: "Image Generator",
      href: "/generator",
      icon: (
        <ImageIcon
          className="h-5 w-5 flex-shrink-0"
          style={{
            color: "hsl(var(--gold))",
          }}
        />
      ),
      onClick: (e: React.MouseEvent) => handleProtectedNavigation("/generator", e),
    },
    {
      label: "Video Generator",
      href: "/video-generator",
      icon: (
        <Video
          className="h-5 w-5 flex-shrink-0"
          style={{
            color: "hsl(var(--gold))",
          }}
        />
      ),
      onClick: (e: React.MouseEvent) => handleProtectedNavigation("/video-generator", e),
    },
    {
      label: "Video Upscale",
      href: "/video-upscale",
      icon: (
        <Maximize2
          className="h-5 w-5 flex-shrink-0"
          style={{
            color: "hsl(var(--gold))",
          }}
        />
      ),
      onClick: (e: React.MouseEvent) => handleProtectedNavigation("/video-upscale", e),
    },
  ];

  const accountLinks = [
    {
      label: "Dashboard",
      href: "/dashboard",
      icon: (
        <LayoutDashboard
          className="h-5 w-5 flex-shrink-0"
          style={{
            color: "hsl(var(--gold))",
          }}
        />
      ),
      onClick: (e: React.MouseEvent) => handleProtectedNavigation("/dashboard", e),
    },
    {
      label: "Buy Credits",
      href: "#",
      icon: (
        <CreditCard
          className="h-5 w-5 flex-shrink-0"
          style={{
            color: "hsl(var(--gold))",
          }}
        />
      ),
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        setShowPricingModal(true);
      },
    },
  ];
  const SidebarContent = () => {
    const { open, animate } = useSidebar();
    return (
      <>
        {/* Navigation Section */}
        <div>
          <div className="px-3 mb-2 overflow-hidden">
            <motion.span
              initial={false}
              animate={{
                width: animate ? (open ? "auto" : 0) : "auto",
                opacity: animate ? (open ? 1 : 0) : 1,
              }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              className="text-xs uppercase tracking-wider opacity-60 whitespace-nowrap inline-block"
              style={{
                color: "hsl(var(--muted-foreground))",
              }}
            >
              Navigation
            </motion.span>
          </div>
          <div className="flex flex-col gap-1">
            {navigationLinks.map((link, idx) => (
              <SidebarLink key={idx} link={link} />
            ))}
          </div>
        </div>

        {/* Account Section */}
        <div className="mt-6">
          <div className="px-3 mb-2 overflow-hidden">
            <motion.span
              initial={false}
              animate={{
                width: animate ? (open ? "auto" : 0) : "auto",
                opacity: animate ? (open ? 1 : 0) : 1,
              }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              className="text-xs uppercase tracking-wider opacity-60 whitespace-nowrap inline-block"
              style={{
                color: "hsl(var(--muted-foreground))",
              }}
            >
              Account
            </motion.span>
          </div>
          <div className="flex flex-col gap-1">
            {accountLinks.map((link, idx) => (
              <div key={idx} onClick={link.onClick}>
                <SidebarLink link={link} />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };


  return (
    <>
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen}>
        <SidebarBody className="justify-between gap-10">
          <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden py-0 my-0">
            <SidebarContent />
          </div>
        </SidebarBody>
      </Sidebar>

      <motion.div
        className="flex-1 relative z-10 overflow-y-auto min-h-screen pb-20 md:pb-0 box-border"
        initial={false}
        animate={{
          marginLeft: isMobile ? "0px" : sidebarOpen ? "300px" : "72px",
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut",
        }}
      >
        {children}
      </motion.div>

      <BottomNavBar />

      <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} />

      <LoginPromptModal
        isOpen={isLoginPromptOpen}
        onClose={() => setIsLoginPromptOpen(false)}
        redirectPath={loginRedirect?.path}
        redirectState={loginRedirect?.state}
      />
    </>
  );
};
